import { FileText } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import agreementsService from "../../services/agreementsService";
import blobService from "../../services/blobService";
import { formatUtcDate } from "../../utils/dateUtils";
import AlphaSettingWidget from "./AlphaSettingWidget";
import AlphaSettingsModal from "./AlphaSettingsModal";
import { toSignedDocuments, type SignedDocument } from "./alphaSignedDocuments";

interface DocumentsState {
  status: "loading" | "loaded" | "failed";
  docs: SignedDocument[];
}

type DocumentFileState =
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "failed" };

const primaryLinkClass = "inline-flex min-h-10 items-center justify-center rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft";
const secondaryLinkClass = "inline-flex min-h-10 items-center justify-center rounded-v7-control border border-v7-ink/25 bg-v7-white px-4 text-sm font-medium text-v7-ink transition-colors hover:border-v7-ink";

const formatSignedDate = (iso: string): string =>
  formatUtcDate(iso, { month: "short", day: "numeric", year: "numeric" });

const DocumentActions: React.FC<{
  doc: SignedDocument;
  file: DocumentFileState | undefined;
}> = ({ doc, file }) => {
  if (!doc.viewable) {
    return <span className="text-xs text-v7-ink/60">Document unavailable.</span>;
  }
  if (!file || file.status === "loading") {
    return <span className="text-xs text-v7-ink/60">Preparing document...</span>;
  }
  if (file.status === "failed") {
    return <span className="text-xs text-v7-rust">Document unavailable right now.</span>;
  }
  return (
    <>
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View ${doc.title}`}
        className={secondaryLinkClass}
      >
        View
      </a>
      <a
        href={file.url}
        download={doc.filename}
        aria-label={`Download ${doc.title}`}
        className={primaryLinkClass}
      >
        Download
      </a>
    </>
  );
};

/**
 * "Your documents" tile for the alpha profile page. Lists the investment
 * advisory agreements the signed-in user has signed and lets them view or
 * download the current PDF of each one.
 */
export default function AlphaYourDocumentsSetting(): React.ReactElement {
  const [documents, setDocuments] = useState<DocumentsState>({ status: "loading", docs: [] });
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<Record<number, DocumentFileState>>({});
  // Request token guarding the agreements fetch against out-of-order
  // resolution: a slower earlier response must never overwrite a newer one.
  const documentsReqRef = useRef(0);

  useEffect(() => {
    const reqId = ++documentsReqRef.current;
    setDocuments({ status: "loading", docs: [] });
    (async () => {
      try {
        const agreements = await agreementsService.getMyAgreements();
        if (reqId !== documentsReqRef.current) return;
        setDocuments({ status: "loaded", docs: toSignedDocuments(agreements) });
      } catch {
        if (reqId !== documentsReqRef.current) return;
        setDocuments({ status: "failed", docs: [] });
      }
    })();
  }, []);

  const docs = documents.docs;

  // PDFs are fetched only while the dialog is open. The blob API URL needs a
  // JWT, so each file is downloaded through blobService and exposed as an
  // object URL. Every URL is revoked when the dialog closes or the tile
  // unmounts, and a response for a previous opening is dropped.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const objectUrls: string[] = [];
    const viewableDocs = docs.filter((doc) => doc.viewable);
    const initial: Record<number, DocumentFileState> = {};
    viewableDocs.forEach((doc) => {
      initial[doc.id] = { status: "loading" };
    });
    setFiles(initial);
    viewableDocs.forEach((doc) => {
      void blobService.downloadRunPlutusFile(doc.folder, doc.filename).then((blob) => {
        if (!active) return;
        const url = URL.createObjectURL(blob);
        objectUrls.push(url);
        setFiles((current) => ({ ...current, [doc.id]: { status: "ready", url } }));
      }).catch(() => {
        if (!active) return;
        setFiles((current) => ({ ...current, [doc.id]: { status: "failed" } }));
      });
    });
    return () => {
      active = false;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      setFiles({});
    };
  }, [docs, open]);

  const count = docs.length;
  const newest = docs[0];
  const canOpen = documents.status === "loaded" && count > 0;

  return (
    <>
      <AlphaSettingWidget
        icon={<FileText className="h-5 w-5" />}
        title="Your documents"
        description="Signed agreements for your account."
        summary={count === 0
          ? "No signed documents"
          : `${count} signed ${count === 1 ? "document" : "documents"}`}
        subtext={newest
          ? `${newest.title} - Signed ${formatSignedDate(newest.signedAtUtc)}`
          : "Agreements you sign with Plutus appear here."}
        isLoading={documents.status === "loading"}
        error={documents.status === "failed" ? "Documents are temporarily unavailable." : undefined}
        actionLabel="View"
        onAction={canOpen ? () => setOpen(true) : undefined}
      />
      <AlphaSettingsModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Your documents"
        maxWidthClass="max-w-3xl"
        description="These are the current versions of the agreements you signed. Your signature details are listed with each document."
      >
        {count === 0 ? (
          <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white py-16 text-center">
            <FileText className="mx-auto mb-3 h-8 w-8 text-v7-ink/30" />
            <p className="text-sm text-v7-ink-soft">No signed documents yet.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-v7-panel border border-v7-ink/15">
            <div className="divide-y divide-v7-ink/10">
              {docs.map((doc) => (
                <div key={doc.id} className="bg-v7-white p-4 transition-colors hover:bg-v7-paper">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <h3 className="text-base font-bold leading-tight text-v7-ink">
                        {doc.title}
                      </h3>
                      <p className="mt-1 text-xs text-v7-ink/60">
                        {doc.signedName ? (
                          <>Signed by <span className="ph-mask">{doc.signedName}</span> on {formatSignedDate(doc.signedAtUtc)}</>
                        ) : (
                          <>Signed on {formatSignedDate(doc.signedAtUtc)}</>
                        )}
                      </p>
                      <div className="mt-2 flex min-w-0 items-center gap-2">
                        <span className="inline-flex shrink-0 rounded border border-v7-ink/15 bg-v7-ink/[0.04] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-v7-ink-soft">
                          Signed
                        </span>
                        <span className="truncate text-xs text-v7-ink/60" title={doc.filename}>
                          {doc.filename}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <DocumentActions doc={doc} file={files[doc.id]} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </AlphaSettingsModal>
    </>
  );
}
